import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { ExternalLink, Play, Trash2, Upload } from "lucide-react";

import {
  useAddMeetingLink,
  useDeleteMeeting,
  useMeetingDetail,
  useRemoveMeetingLink,
  useUpdateMeeting,
} from "@/application/use-cases/meetings/use-meetings.queries";
import { useDecisionsList } from "@/application/use-cases/decisions/use-decisions.queries";
import { useRisksList } from "@/application/use-cases/risks/use-risks.queries";
import { useOpenQuestionsList } from "@/application/use-cases/open-questions/use-open-questions.queries";
import { useTasksList } from "@/application/use-cases/tasks/use-tasks.queries";
import { meetingsRepository } from "@/infrastructure/repositories/meetings.supabase.repository";
import { useQueryClient } from "@tanstack/react-query";
import { meetingsKeys } from "@/application/query-keys/meetings.keys";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { Label } from "@monorepo-apps/ui/src/components/label";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";
import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@monorepo-apps/ui/src/components/alert-dialog";
import { StatusDot, taskStatusToTone } from "@/presentation/components/status/status-dot";
import type { StatusDotTone } from "@/presentation/components/status/status-dot";
import { X } from "lucide-react";
import type { EntityType, MeetingRow } from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/meetings/$id")({
  component: MeetingDetailPage,
});

function MeetingDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const detailQ = useMeetingDetail(id);
  const update = useUpdateMeeting();
  const addLink = useAddMeetingLink();
  const removeLink = useRemoveMeetingLink();
  const deleteMeeting = useDeleteMeeting();
  const decisionsQ = useDecisionsList();
  const risksQ = useRisksList();
  const questionsQ = useOpenQuestionsList();
  const tasksQ = useTasksList();
  const [confirmDelete, setConfirmDelete] = useState(false);

  // realtime subscription so the detail refreshes when other viewers edit
  const queryClient = useQueryClient();
  useEffect(() => {
    return meetingsRepository.subscribeToChanges(() => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.all });
    });
  }, [queryClient]);

  const [linkType, setLinkType] = useState<EntityType>("decision");
  const [linkTarget, setLinkTarget] = useState("");

  if (!detailQ.data) {
    return (
      <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col">
        <PageHeader title="Meeting" />
        <p className="text-muted-foreground px-6 py-8 text-xs italic lg:px-10">Loading…</p>
      </div>
    );
  }
  const m = detailQ.data;

  const linkedDecisions = m.links
    .filter((l) => l.entity_type === "decision")
    .map((l) => decisionsQ.data?.find((d) => d.id === l.entity_id))
    .filter(Boolean);
  const linkedRisks = m.links
    .filter((l) => l.entity_type === "risk")
    .map((l) => risksQ.data?.find((r) => r.id === l.entity_id))
    .filter(Boolean);
  const linkedQuestions = m.links
    .filter((l) => l.entity_type === "open_question")
    .map((l) => questionsQ.data?.find((q) => q.id === l.entity_id))
    .filter(Boolean);
  const linkedTasks = m.links
    .filter((l) => l.entity_type === "task")
    .map((l) => tasksQ.data?.find((t) => t.id === l.entity_id))
    .filter(Boolean);

  const linkOptions =
    linkType === "decision"
      ? (decisionsQ.data ?? []).map((d) => ({ id: d.id, label: d.title }))
      : linkType === "risk"
        ? (risksQ.data ?? []).map((r) => ({ id: r.id, label: `${r.code} ${r.title}` }))
        : linkType === "open_question"
          ? (questionsQ.data ?? []).map((q) => ({ id: q.id, label: q.question.slice(0, 60) }))
          : (tasksQ.data ?? []).map((t) => ({ id: t.id, label: t.title }));

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col">
      <PageHeader
        eyebrow="Meeting"
        title={m.title}
        summary={format(new Date(m.meeting_at), "EEE d MMM yyyy, HH:mm")}
        actions={
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground hover:text-destructive print:hidden"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="size-3.5" />
            Delete
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-x-10 gap-y-6 px-6 py-6 lg:grid-cols-[1fr_320px] lg:px-10">
        <section className="flex flex-col gap-6">
          <Field label="Title">
            <Input
              defaultValue={m.title}
              onBlur={(e) =>
                e.target.value !== m.title &&
                update.mutate({ id: m.id, patch: { title: e.target.value } })
              }
            />
          </Field>

          <Field label="Agenda">
            <Textarea
              defaultValue={m.agenda ?? ""}
              rows={3}
              onBlur={(e) =>
                e.target.value !== (m.agenda ?? "") &&
                update.mutate({ id: m.id, patch: { agenda: e.target.value } })
              }
            />
          </Field>

          <Field label="Notes">
            <Textarea
              defaultValue={m.notes ?? ""}
              rows={10}
              placeholder="What was discussed, decided, blocked, raised…"
              onBlur={(e) =>
                e.target.value !== (m.notes ?? "") &&
                update.mutate({ id: m.id, patch: { notes: e.target.value } })
              }
            />
          </Field>

          <RecordingSection
            meetingId={m.id}
            recordingUrl={m.recording_url}
            transcriptUrl={m.transcript_url}
            summaryUrl={m.summary_url}
          />

          <PresentationSection meetingId={m.id} presentationHtml={m.presentation_html} />
        </section>

        <aside className="border-border/40 flex flex-col gap-6 border-l pl-6 text-xs">
          <div>
            <Label className="text-muted-foreground font-data text-[10px] tracking-[0.18em] uppercase">
              When
            </Label>
            <Input
              type="datetime-local"
              defaultValue={new Date(m.meeting_at).toISOString().slice(0, 16)}
              className="font-data mt-1 h-8 text-[11px] tabular-nums"
              onBlur={(e) =>
                update.mutate({
                  id: m.id,
                  patch: { meeting_at: new Date(e.target.value).toISOString() },
                })
              }
            />
          </div>

          <div>
            <Label className="text-muted-foreground font-data text-[10px] tracking-[0.18em] uppercase">
              Linked items
            </Label>
            <ul className="mt-2 flex flex-col gap-1">
              {linkedDecisions.map((d) =>
                d ? (
                  <LinkChip
                    key={d.id}
                    href={`/decisions?id=${d.id}`}
                    tone={d.status}
                    label={d.title}
                    disabled={removeLink.isPending}
                    onRemove={() => {
                      const linkRow = m.links.find(
                        (l) => l.entity_type === "decision" && l.entity_id === d.id
                      );
                      if (linkRow) removeLink.mutate({ linkId: linkRow.id, meetingId: m.id });
                    }}
                  />
                ) : null
              )}
              {linkedRisks.map((r) =>
                r ? (
                  <LinkChip
                    key={r.id}
                    href={`/risks?id=${r.id}`}
                    tone={r.rag}
                    label={`${r.code} ${r.title}`}
                    disabled={removeLink.isPending}
                    onRemove={() => {
                      const linkRow = m.links.find(
                        (l) => l.entity_type === "risk" && l.entity_id === r.id
                      );
                      if (linkRow) removeLink.mutate({ linkId: linkRow.id, meetingId: m.id });
                    }}
                  />
                ) : null
              )}
              {linkedQuestions.map((q) =>
                q ? (
                  <LinkChip
                    key={q.id}
                    href={`/open-questions/${q.id}`}
                    tone={q.status}
                    label={q.question}
                    disabled={removeLink.isPending}
                    onRemove={() => {
                      const linkRow = m.links.find(
                        (l) => l.entity_type === "open_question" && l.entity_id === q.id
                      );
                      if (linkRow) removeLink.mutate({ linkId: linkRow.id, meetingId: m.id });
                    }}
                  />
                ) : null
              )}
              {linkedTasks.map((t) =>
                t ? (
                  <LinkChip
                    key={t.id}
                    href={`/tasks?id=${t.id}`}
                    tone={taskStatusToTone(t.status)}
                    label={t.title}
                    disabled={removeLink.isPending}
                    onRemove={() => {
                      const linkRow = m.links.find(
                        (l) => l.entity_type === "task" && l.entity_id === t.id
                      );
                      if (linkRow) removeLink.mutate({ linkId: linkRow.id, meetingId: m.id });
                    }}
                  />
                ) : null
              )}
              {m.links.length === 0 ? (
                <p className="text-muted-foreground text-xs italic">No items linked yet.</p>
              ) : null}
            </ul>
          </div>

          <div className="flex flex-col gap-2 print:hidden">
            <Label className="text-muted-foreground font-data text-[10px] tracking-[0.18em] uppercase">
              Add link
            </Label>
            <Select value={linkType} onValueChange={(v) => setLinkType(v as EntityType)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="decision">Decision</SelectItem>
                <SelectItem value="risk">Risk</SelectItem>
                <SelectItem value="open_question">Open question</SelectItem>
                <SelectItem value="task">Task</SelectItem>
              </SelectContent>
            </Select>
            <Select value={linkTarget} onValueChange={setLinkTarget}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Pick one…" />
              </SelectTrigger>
              <SelectContent>
                {linkOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              disabled={!linkTarget || addLink.isPending}
              onClick={() => {
                addLink.mutate(
                  { meetingId: m.id, entityType: linkType, entityId: linkTarget },
                  { onSuccess: () => setLinkTarget("") }
                );
              }}
            >
              Link
            </Button>
          </div>
        </aside>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              "{m.title}" will be permanently removed, along with its agenda, notes, attendees, and
              links.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMeeting.mutate(m.id, {
                  onSuccess: () => {
                    setConfirmDelete(false);
                    navigate({ to: "/meetings" });
                  },
                });
              }}
              disabled={deleteMeeting.isPending}
            >
              {deleteMeeting.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground font-data text-[10px] tracking-[0.18em] uppercase">
        {label}
      </Label>
      {children}
    </div>
  );
}

function RecordingSection({
  meetingId,
  recordingUrl,
  transcriptUrl,
  summaryUrl,
}: {
  meetingId: string;
  recordingUrl: string | null;
  transcriptUrl: string | null;
  summaryUrl: string | null;
}) {
  const update = useUpdateMeeting();
  const onBlurFor =
    (field: "recording_url" | "transcript_url" | "summary_url", current: string | null) =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      const next = e.target.value.trim();
      if (next === (current ?? "")) return;
      update.mutate({ id: meetingId, patch: { [field]: next || null } as Partial<MeetingRow> });
    };

  return (
    <div className="flex flex-col gap-3">
      <Label className="text-muted-foreground font-data text-[10px] tracking-[0.18em] uppercase">
        Recording
      </Label>
      <UrlRow
        label="Recording"
        defaultValue={recordingUrl}
        onBlur={onBlurFor("recording_url", recordingUrl)}
      />
      <UrlRow
        label="Transcript"
        defaultValue={transcriptUrl}
        onBlur={onBlurFor("transcript_url", transcriptUrl)}
      />
      <UrlRow
        label="Summary"
        defaultValue={summaryUrl}
        onBlur={onBlurFor("summary_url", summaryUrl)}
      />
    </div>
  );
}

function UrlRow({
  label,
  defaultValue,
  onBlur,
}: {
  label: string;
  defaultValue: string | null;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-muted-foreground w-20 shrink-0 text-[11px]">{label}</Label>
      <Input
        type="url"
        placeholder="https://fathom.video/…"
        defaultValue={defaultValue ?? ""}
        onBlur={onBlur}
        className="h-8 text-xs"
      />
      {defaultValue ? (
        <a
          href={defaultValue}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-8 shrink-0 items-center justify-center rounded-sm transition"
          aria-label={`Open ${label} in new tab`}
          title="Open"
        >
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="size-8 shrink-0" aria-hidden />
      )}
    </div>
  );
}

function PresentationSection({
  meetingId,
  presentationHtml,
}: {
  meetingId: string;
  presentationHtml: string | null;
}) {
  const update = useUpdateMeeting();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const hasContent = !!presentationHtml && presentationHtml.trim().length > 0;

  const writeFile = async (file: File) => {
    setLoading(true);
    try {
      const html = await file.text();
      update.mutate({ id: meetingId, patch: { presentation_html: html } });
    } finally {
      setLoading(false);
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (hasContent) {
      setPendingFile(file);
      return;
    }
    void writeFile(file);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-muted-foreground font-data text-[10px] tracking-[0.18em] uppercase">
          Presentation
        </Label>
        <div className="flex items-center gap-2 print:hidden">
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,text/html"
            onChange={onFilePicked}
            className="hidden"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={loading || update.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="size-3.5" />
            Load HTML file
          </Button>
          <Button size="sm" disabled={!hasContent} asChild={hasContent}>
            {hasContent ? (
              <Link to="/meetings/$id/present" params={{ id: meetingId }}>
                <Play className="size-3.5" />
                Present
              </Link>
            ) : (
              <span>
                <Play className="size-3.5" />
                Present
              </span>
            )}
          </Button>
        </div>
      </div>
      <Textarea
        key={presentationHtml ?? ""}
        defaultValue={presentationHtml ?? ""}
        rows={10}
        placeholder="Paste full HTML for a self-contained presentation deck, or load one with the button above…"
        className="font-mono text-xs"
        onBlur={(e) => {
          const next = e.target.value;
          if (next === (presentationHtml ?? "")) return;
          update.mutate({ id: meetingId, patch: { presentation_html: next || null } });
        }}
      />

      <AlertDialog
        open={!!pendingFile}
        onOpenChange={(open) => {
          if (!open) {
            setPendingFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace existing presentation?</AlertDialogTitle>
            <AlertDialogDescription>
              The current HTML in this meeting will be overwritten with the contents of{" "}
              <span className="font-medium">{pendingFile?.name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingFile && void writeFile(pendingFile)}
              disabled={loading}
            >
              {loading ? "Loading…" : "Replace"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LinkChip({
  href,
  tone,
  label,
  onRemove,
  disabled,
}: {
  href: string;
  tone: StatusDotTone;
  label: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <li className="border-border/60 hover:border-border flex items-center gap-2 rounded-sm border py-1.5 pr-1 pl-2 print:break-inside-avoid">
      <StatusDot tone={tone} />
      <Link to={href} className="flex-1 truncate text-xs hover:underline">
        {label}
      </Link>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive flex size-6 shrink-0 items-center justify-center rounded-sm transition disabled:opacity-50 print:hidden"
        aria-label={`Unlink ${label}`}
        title="Unlink"
      >
        <X className="size-3.5" />
      </button>
    </li>
  );
}
